import React, { useState, useEffect } from "react";
import { Modal, TextInput, Select, NumberInput, Button, Group, Stack, Anchor } from "@mantine/core";
import { useForm } from "@mantine/form";
import { offersServices } from "../../services/offersServices";

const CreateOfferModal = ({ isVisible, onCancel, onSuccess }) => {
    const [verticals, setVerticals] = useState([]);
    const [partnerships, setPartnerships] = useState([]);
    const [isVerticalModalVisible, setIsVerticalModalVisible] = useState(false);
    const [isPartnershipModalVisible, setIsPartnershipModalVisible] = useState(false);

    const form = useForm({
        initialValues: {
            geo: "",
            name: "",
            vertical: "",
            partnership: "",
            payout: 0,
            approvalRate: 0,
            maxLeadPrice: 0, // Added missing field if needed, or remove if not in original form but in table
        },
        validate: {
            geo: (value) => (/^[A-Z]{2}$/.test(value) ? null : "GEO must be two uppercase letters (e.g., PL, SK)"),
            name: (value) => (value ? null : "Please input offer name"),
            vertical: (value) => (value ? null : "Please select vertical"),
            partnership: (value) => (value ? null : "Please select partnership"),
            payout: (value) => (value >= 0 ? null : "Payout must be positive"),
            approvalRate: (value) => (value >= 0 && value <= 100 ? null : "Approval rate must be between 0 and 100"),
        },
    });

    const verticalForm = useForm({
        initialValues: { name: "" },
        validate: { name: (value) => (value ? null : "Please input vertical name") },
    });

    const partnershipForm = useForm({
        initialValues: { name: "" },
        validate: { name: (value) => (value ? null : "Please input partnership name") },
    });

    useEffect(() => {
        if (isVisible) {
            loadData();
            form.reset();
        }
    }, [isVisible, form]);

    const loadData = async () => {
        try {
            const [verticalsData, partnershipsData] = await Promise.all([
                offersServices.getVerticals(),
                offersServices.getPartnerships()
            ]);
            setVerticals(verticalsData.map(v => ({ value: v.id.toString(), label: v.name })));
            setPartnerships(partnershipsData.map(p => ({ value: p.id.toString(), label: p.name })));
        } catch (error) {
            console.error("Error loading data:", error);
        }
    };

    const handleSubmit = async (values) => {
        try {
            await offersServices.createOffer({
                ...values,
                vertical: parseInt(values.vertical),
                partnership: parseInt(values.partnership)
            });
            form.reset();
            onSuccess();
        } catch (error) {
            console.error("Error creating offer:", error);
        }
    };

    const handleCreateVertical = async (values) => {
        try {
            await offersServices.createVertical(values);
            verticalForm.reset();
            setIsVerticalModalVisible(false);
            loadData();
        } catch (error) {
            console.error("Error creating vertical:", error);
        }
    };

    const handleCreatePartnership = async (values) => {
        try {
            await offersServices.createPartnership(values);
            partnershipForm.reset();
            setIsPartnershipModalVisible(false);
            loadData();
        } catch (error) {
            console.error("Error creating partnership:", error);
        }
    };

    return (
        <>
            <Modal
                opened={isVisible}
                onClose={onCancel}
                title="Create New Offer"
                size="lg"
            >
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack>
                        <Group grow>
                            <TextInput
                                label="GEO"
                                placeholder="e.g. PL"
                                maxLength={2}
                                style={{ textTransform: "uppercase" }}
                                {...form.getInputProps("geo")}
                                onChange={(event) => {
                                    form.setFieldValue("geo", event.currentTarget.value.toUpperCase());
                                }}
                            />
                            <TextInput
                                label="Offer Name"
                                placeholder="Offer Name"
                                {...form.getInputProps("name")}
                            />
                        </Group>

                        <Select
                            label="Vertical"
                            placeholder="Select vertical"
                            data={verticals}
                            searchable
                            nothingFoundMessage={
                                <Anchor component="button" onClick={() => setIsVerticalModalVisible(true)}>
                                    + Add New Vertical
                                </Anchor>
                            }
                            {...form.getInputProps("vertical")}
                        />

                        <Select
                            label="Partnership"
                            placeholder="Select partnership"
                            data={partnerships}
                            searchable
                            nothingFoundMessage={
                                <Anchor component="button" onClick={() => setIsPartnershipModalVisible(true)}>
                                    + Add New Partnership
                                </Anchor>
                            }
                            {...form.getInputProps("partnership")}
                        />

                        <Group grow>
                            <NumberInput
                                label="Payout"
                                min={0}
                                {...form.getInputProps("payout")}
                            />
                            <NumberInput
                                label="Approval Rate (%)"
                                min={0}
                                max={100}
                                {...form.getInputProps("approvalRate")}
                            />
                        </Group>

                        <Group justify="flex-end" mt="md">
                            <Button variant="default" onClick={onCancel}>Cancel</Button>
                            <Button type="submit">Create Offer</Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>

            <Modal
                opened={isVerticalModalVisible}
                onClose={() => setIsVerticalModalVisible(false)}
                title="Create New Vertical"
            >
                <form onSubmit={verticalForm.onSubmit(handleCreateVertical)}>
                    <Stack>
                        <TextInput
                            label="Vertical Name"
                            placeholder="Vertical Name"
                            {...verticalForm.getInputProps("name")}
                        />
                        <Group justify="flex-end" mt="md">
                            <Button variant="default" onClick={() => setIsVerticalModalVisible(false)}>Cancel</Button>
                            <Button type="submit">Create Vertical</Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>

            <Modal
                opened={isPartnershipModalVisible}
                onClose={() => setIsPartnershipModalVisible(false)}
                title="Create New Partnership"
            >
                <form onSubmit={partnershipForm.onSubmit(handleCreatePartnership)}>
                    <Stack>
                        <TextInput
                            label="Partnership Name"
                            placeholder="Partnership Name"
                            {...partnershipForm.getInputProps("name")}
                        />
                        <Group justify="flex-end" mt="md">
                            <Button variant="default" onClick={() => setIsPartnershipModalVisible(false)}>Cancel</Button>
                            <Button type="submit">Create Partnership</Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>
        </>
    );
};

export default CreateOfferModal;
